import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SEOSettings {
  google_analytics_id?: string;
  facebook_pixel_id?: string;
  custom_head_scripts?: string;
}

// Validation patterns for tracking IDs
const GA_ID_PATTERN = /^(UA-\d{4,10}-\d{1,4}|G-[A-Z0-9]{10,12})$/;
const FB_PIXEL_PATTERN = /^\d{15,16}$/;

// Allowed domains for custom scripts
const ALLOWED_SCRIPT_DOMAINS = [
  'googletagmanager.com',
  'google-analytics.com',
  'connect.facebook.net',
  'facebook.com',
  'analytics.google.com',
  'www.googleadservices.com',
  'googleads.g.doubleclick.net',
  'snap.licdn.com',
  'static.hotjar.com',
  'cdn.segment.com',
  'plausible.io',
  'cdn.mxpnl.com',
];

function validateGAId(id: string): boolean {
  return GA_ID_PATTERN.test(id.trim());
}

function validatePixelId(id: string): boolean {
  return FB_PIXEL_PATTERN.test(id.trim());
}

function sanitizeScriptSrc(src: string): string | null {
  try {
    const url = new URL(src);
    const isAllowed = ALLOWED_SCRIPT_DOMAINS.some(domain => 
      url.hostname === domain || url.hostname.endsWith('.' + domain)
    );
    return isAllowed ? src : null;
  } catch {
    return null;
  }
}

function escapeForScript(value: string): string {
  // Escape characters that could break out of string context
  return value
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/</g, '\\x3c')
    .replace(/>/g, '\\x3e')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

export const TrackingScripts = () => {
  const [settings, setSettings] = useState<SEOSettings | null>(null);

  useEffect(() => {
    loadSEOSettings();
  }, []);

  const loadSEOSettings = async () => {
    try {
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "seo_settings")
        .maybeSingle();

      if (data?.value) {
        setSettings(data.value as SEOSettings);
      }
    } catch (error) {
      console.error("Error loading SEO settings:", error);
    }
  };

  useEffect(() => {
    if (!settings) return;

    // Only inject scripts in production
    const isProduction = window.location.hostname === "inclusia.pro";
    if (!isProduction) {
      console.log("[TrackingScripts] Development mode - scripts not injected");
      return;
    }

    // Inject Google Analytics with validation
    if (settings.google_analytics_id) {
      const gaId = settings.google_analytics_id.trim();
      if (validateGAId(gaId)) {
        injectGoogleAnalytics(gaId);
      } else {
        console.warn("[TrackingScripts] Invalid Google Analytics ID format, skipping injection");
      }
    }

    // Inject Facebook Pixel with validation
    if (settings.facebook_pixel_id) {
      const pixelId = settings.facebook_pixel_id.trim();
      if (validatePixelId(pixelId)) {
        injectFacebookPixel(pixelId);
      } else {
        console.warn("[TrackingScripts] Invalid Facebook Pixel ID format, skipping injection");
      }
    }

    // Inject custom head scripts with sanitization
    if (settings.custom_head_scripts) {
      injectCustomScripts(settings.custom_head_scripts);
    }
  }, [settings]);

  return null;
};

// Google Consent Mode v2 - Helper function to update consent
export function updateGoogleConsent(analyticsGranted: boolean, marketingGranted: boolean) {
  if (!(window as any).gtag) return;
  
  (window as any).gtag('consent', 'update', {
    'analytics_storage': analyticsGranted ? 'granted' : 'denied',
    'ad_storage': marketingGranted ? 'granted' : 'denied',
    'ad_user_data': marketingGranted ? 'granted' : 'denied',
    'ad_personalization': marketingGranted ? 'granted' : 'denied',
  });
  
  console.log("[TrackingScripts] Consent updated:", { analyticsGranted, marketingGranted });
}

function injectGoogleAnalytics(gaId: string) {
  // Check if already injected
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}"]`)) {
    return;
  }

  // CONSENT MODE v2: Set default consent BEFORE loading gtag
  const consentScript = document.createElement("script");
  consentScript.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    
    // Set default consent to denied (GDPR compliant)
    gtag('consent', 'default', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'wait_for_update': 500
    });
  `;
  document.head.appendChild(consentScript);

  // Then load gtag script
  const gtagScript = document.createElement("script");
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  document.head.appendChild(gtagScript);

  // Config script (without redefining gtag)
  const escapedGaId = escapeForScript(gaId);
  const configScript = document.createElement("script");
  configScript.textContent = `
    gtag('js', new Date());
    gtag('config', '${escapedGaId}', {
      page_path: window.location.pathname,
      anonymize_ip: true
    });
  `;
  document.head.appendChild(configScript);

  // Apply saved consent from localStorage if exists
  const savedConsent = localStorage.getItem("cookie-consent");
  if (savedConsent) {
    try {
      const prefs = JSON.parse(savedConsent);
      // Use setTimeout to ensure gtag is loaded
      setTimeout(() => {
        if ((window as any).gtag) {
          (window as any).gtag('consent', 'update', {
            'analytics_storage': prefs.analytics ? 'granted' : 'denied',
            'ad_storage': prefs.marketing ? 'granted' : 'denied',
            'ad_user_data': prefs.marketing ? 'granted' : 'denied',
            'ad_personalization': prefs.marketing ? 'granted' : 'denied',
          });
          console.log("[TrackingScripts] Applied saved consent:", prefs);
        }
      }, 100);
    } catch (e) {
      console.warn("[TrackingScripts] Error parsing saved consent:", e);
    }
  }

  console.log("[TrackingScripts] Google Analytics injected with Consent Mode v2");
}

function injectFacebookPixel(pixelId: string) {
  // Check if already injected
  if ((window as any).fbq) {
    return;
  }

  // Use escaped pixel ID
  const escapedPixelId = escapeForScript(pixelId);

  // Create and inject Facebook Pixel script
  const fbScript = document.createElement("script");
  fbScript.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${escapedPixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(fbScript);

  // Add noscript fallback with encoded pixel ID
  const noscript = document.createElement("noscript");
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);

  console.log("[TrackingScripts] Facebook Pixel injected");
}

function injectCustomScripts(scripts: string) {
  if (!scripts.trim()) return;

  const customScriptId = "custom-head-scripts";
  
  // Remove existing custom scripts
  const existing = document.getElementById(customScriptId);
  if (existing) {
    existing.remove();
  }

  // Parse scripts safely using DOMParser instead of innerHTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(scripts, 'text/html');
  const scriptTags = doc.querySelectorAll('script');
  
  let injectedCount = 0;
  
  scriptTags.forEach((script) => {
    if (script.src) {
      // Validate external script sources
      const sanitizedSrc = sanitizeScriptSrc(script.src);
      if (sanitizedSrc) {
        const newScript = document.createElement("script");
        newScript.src = sanitizedSrc;
        newScript.async = true;
        newScript.dataset.customScript = "true";
        document.head.appendChild(newScript);
        injectedCount++;
      } else {
        console.warn("[TrackingScripts] Blocked script from untrusted domain:", script.src);
      }
    }
    // Note: Inline scripts from custom_head_scripts are intentionally not executed
    // as they pose a higher XSS risk. Only whitelisted external scripts are allowed.
  });

  if (injectedCount > 0) {
    console.log(`[TrackingScripts] ${injectedCount} custom script(s) injected`);
  }
}

export default TrackingScripts;
